import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { getApiUrl } from "../config/api";
import { useBilling } from "../contexts/BillingContext";

type Platform = "ozon" | "wildberries" | "yandex";

type Store = {
  id: string;
  userId: string;
  platform: Platform;
  name: string;
  credentials: Record<string, any>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

const platformFields: Record<
  Platform,
  Array<{ key: string; label: string; required?: boolean }>
> = {
  ozon: [
    { key: "apiKey", label: "Api Key", required: true },
    { key: "clientId", label: "Client ID", required: true },
    { key: "clientSecret", label: "Client Secret" },
    { key: "sellerId", label: "Seller ID" },
  ],
  wildberries: [{ key: "apiKey", label: "API Key", required: true }],
  yandex: [
    { key: "apiKey", label: "API Key", required: true },
    { key: "campaignId", label: "Campaign ID", required: true },
    { key: "businessId", label: "Business ID", required: true },
  ],
};

const ModalPortal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (typeof document === "undefined") return null as any;
  return ReactDOM.createPortal(children, document.body);
};

const AddStoreModal = React.memo(
  ({
    open,
    onClose,
    onSubmit,
  }: {
    open: boolean;
    onClose: () => void;
    onSubmit: (data: {
      platform: Platform;
      name: string;
      credentials: Record<string, string>;
    }) => Promise<void> | void;
  }) => {
    const [platform, setPlatform] = useState<Platform>("ozon");
    const [name, setName] = useState("");
    const [creds, setCreds] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [submitError, setSubmitError] = useState<string | null>(null);

    useEffect(() => {
      if (open) {
        setPlatform("ozon");
        setName("");
        setCreds({});
        setFieldErrors({});
        setSubmitError(null);
        setSaving(false);
      }
    }, [open]);

    if (!open) return null;

    const fields = platformFields[platform];

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setSubmitError(null);
      setFieldErrors({});
      const required = fields.filter((f) => f.required);
      const missing = required.filter(
        (f) => !creds[f.key] || !creds[f.key].trim()
      );
      const errs: Record<string, string> = {};
      if (!name.trim()) errs.name = "Укажите название магазина";
      for (const m of missing) errs[m.key] = "Обязательное поле";
      if (Object.keys(errs).length > 0) {
        setFieldErrors(errs);
        return;
      }
      try {
        setSaving(true);
        await onSubmit({ platform, name, credentials: creds });
        onClose();
      } catch (e: any) {
        setSubmitError(e?.message || "Ошибка сохранения");
      } finally {
        setSaving(false);
      }
    };

    return (
      <ModalPortal>
        <div className="fixed inset-0 m-0 p-0 z-[100]">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
            onClick={() => !saving && onClose()}
          />
          <div className="relative h-full w-full flex items-center justify-center">
            <div className="w-full max-w-2xl mx-4 rounded-2xl bg-white shadow-2xl ring-1 ring-gray-200">
              <div className="flex items-center justify-between px-6 py-4 border-b">
                <div>
                  <h2 className="text-lg font-semibold">Добавить магазин</h2>
                  <p className="text-xs text-gray-500">
                    Подключите магазин, чтобы получать отзывы
                  </p>
                </div>
                <button
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-gray-100 text-gray-500"
                  onClick={() => !saving && onClose()}
                  type="button"
                  aria-label="Закрыть"
                >
                  ✕
                </button>
              </div>
              <form className="px-6 py-5 space-y-5" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="block text-xs font-medium text-gray-700">
                      Название <span className="text-red-500">*</span>
                    </label>
                    <input
                      className={`w-full rounded-lg border px-3 py-2 text-sm shadow-sm outline-none transition focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${fieldErrors.name ? "border-red-400" : "border-gray-200"}`}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Например: Основной Ozon"
                      type="text"
                    />
                    {fieldErrors.name ? (
                      <div className="text-xs text-red-500">
                        {fieldErrors.name}
                      </div>
                    ) : null}
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-xs font-medium text-gray-700">
                      Платформа
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {(["ozon", "wildberries", "yandex"] as Platform[]).map(
                        (p) => (
                          <button
                            key={p}
                            type="button"
                            onClick={() => {
                              setPlatform(p);
                              setFieldErrors({});
                            }}
                            className={`rounded-lg border px-3 py-2 text-sm capitalize transition ${platform === p ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"}`}
                          >
                            {p}
                          </button>
                        )
                      )}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {fields.map((f) => (
                    <div key={f.key} className="space-y-1.5">
                      <label className="block text-xs font-medium text-gray-700">
                        {f.label}{" "}
                        {f.required ? (
                          <span className="text-red-500">*</span>
                        ) : null}
                      </label>
                      <input
                        className={`w-full rounded-lg border px-3 py-2 text-sm shadow-sm outline-none transition focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${fieldErrors[f.key] ? "border-red-400" : "border-gray-200"}`}
                        value={creds[f.key] || ""}
                        onChange={(e) =>
                          setCreds((c) => ({ ...c, [f.key]: e.target.value }))
                        }
                        placeholder={f.label}
                        type={
                          platform === "yandex" && f.key === "businessId"
                            ? "number"
                            : "text"
                        }
                      />
                      {fieldErrors[f.key] ? (
                        <div className="text-xs text-red-500">
                          {fieldErrors[f.key]}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
                {submitError ? (
                  <div className="text-sm text-red-600">{submitError}</div>
                ) : null}
                <div className="flex items-center justify-end gap-2 pt-2">
                  <button
                    type="button"
                    className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    onClick={() => onClose()}
                    disabled={saving}
                  >
                    Отмена
                  </button>
                  <button
                    type="submit"
                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-60"
                    disabled={saving}
                  >
                    {saving ? "Сохранение..." : "Создать"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </ModalPortal>
    );
  }
);

const Stores: React.FC = () => {
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [maxStores, setMaxStores] = useState<number | null>(null);
  const { data: billing, refresh: refreshBilling } = useBilling();
  const [ozonPremium, setOzonPremium] = useState<
    Record<string, "checking" | "ok" | "no" | "error">
  >({});

  const authHeaders = useMemo(() => {
    const token = localStorage.getItem("token");
    return {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    } as Record<string, string>;
  }, []);

  const fetchStores = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(getApiUrl("/api/stores"), {
        headers: authHeaders,
      });
      if (!res.ok) throw new Error("Не удалось загрузить магазины");
      const data = await res.json();
      setStores(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e?.message || "Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  useEffect(() => {
    if (!billing) {
      refreshBilling();
      return;
    }
    const m =
      typeof billing?.maxStores === "number"
        ? billing.maxStores
        : (billing?.subscription?.plan?.maxStores ?? null);
    setMaxStores(typeof m === "number" ? m : null);
  }, [billing, refreshBilling]);

  // Проверочные запросы для OZON: Premium Plus доступ к отзывам
  useEffect(() => {
    const runChecks = async () => {
      const ozonStores = stores.filter((s) => s.platform === "ozon");
      if (ozonStores.length === 0) return;
      // Инициализируем статусы "checking"
      setOzonPremium((prev) => {
        const next = { ...prev };
        for (const s of ozonStores) {
          if (!next[s.id]) next[s.id] = "checking";
        }
        return next;
      });

      await Promise.all(
        ozonStores.map(async (s) => {
          try {
            const url = new URL(getApiUrl("/api/ozon-reviews"));
            url.searchParams.set("limit", "20");
            url.searchParams.set("storeId", s.id);
            const res = await fetch(url.toString(), { headers: authHeaders });
            let data: any = null;
            let msg = "";
            try {
              data = await res.json();
              msg = JSON.stringify(data || {}).toLowerCase();
            } catch (_) {
              data = null;
            }
            const requiresPremium =
              (data &&
                typeof data === "object" &&
                data.info &&
                data.info.premiumPlus === false) ||
              msg.includes("premium plus") ||
              msg.includes("permissiondenied");
            if (requiresPremium) {
              setOzonPremium((prev) => ({ ...prev, [s.id]: "no" }));
              return;
            }
            if (res.ok) {
              setOzonPremium((prev) => ({ ...prev, [s.id]: "ok" }));
              return;
            }
            setOzonPremium((prev) => ({ ...prev, [s.id]: "error" }));
          } catch (_) {
            setOzonPremium((prev) => ({ ...prev, [s.id]: "error" }));
          }
        })
      );
    };
    runChecks();
  }, [stores, authHeaders]);

  const handleSubmit = useCallback(
    async (payload: {
      platform: Platform;
      name: string;
      credentials: Record<string, string>;
    }) => {
      const res = await fetch(getApiUrl("/api/stores"), {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.message || "Не удалось создать магазин");
      }
      await fetchStores();
    },
    [authHeaders, fetchStores]
  );

  const deleteStore = useCallback(
    async (storeId: string) => {
      if (!window.confirm("Удалить магазин? Это действие необратимо.")) return;
      const res = await fetch(getApiUrl(`/api/stores/${storeId}`), {
        method: "DELETE",
        headers: authHeaders,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(data?.message || "Не удалось удалить магазин");
        return;
      }
      await fetchStores();
    },
    [authHeaders, fetchStores]
  );

  const getPlatformBadgeClasses = (platform: Platform) => {
    if (platform === "ozon") return "bg-blue-100 text-blue-700 border-blue-200";
    if (platform === "wildberries")
      return "bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200";
    return "bg-yellow-100 text-yellow-700 border-yellow-200"; // yandex
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Управление магазинами</h1>
        <button className="btn-primary" onClick={() => setModalOpen(true)}>
          Добавить магазин
        </button>
      </div>
      <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
        Лимит магазинов по тарифу:{" "}
        {typeof maxStores === "number" ? maxStores : "—"}. Подключено:{" "}
        {stores.length}
      </div>
      {error ? <div className="text-red-600">{error}</div> : null}
      {loading ? (
        <div>Загрузка...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {stores.map((s) => (
            <div
              key={s.id}
              className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
            >
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <div className="inline-flex items-center gap-2">
                    <span
                      className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border ${getPlatformBadgeClasses(
                        s.platform
                      )}`}
                    >
                      {s.platform}
                    </span>
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded-full ${s.isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}
                    >
                      {s.isActive ? "Активен" : "Отключен"}
                    </span>
                  </div>
                  <div className="text-base font-semibold text-gray-900">
                    {s.name}
                  </div>
                </div>
                <button
                  className="text-red-600 hover:text-red-700 text-sm"
                  onClick={() => deleteStore(s.id)}
                >
                  Удалить
                </button>
              </div>

              <div className="mt-3 grid grid-cols-1 gap-3 text-sm">
                {s.platform === "ozon" ? (
                  <>
                    <div className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
                      <span className="text-gray-600">Client ID</span>
                      <span className="font-mono text-gray-900">
                        {s.credentials?.clientId || "—"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
                      <span className="text-gray-600">Client Secret</span>
                      <span className="font-mono text-gray-900">
                        {s.credentials?.clientSecret || "—"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
                      <div className="flex items-center gap-2 text-gray-700">
                        <span className="font-medium">Premium Plus</span>
                        <span className="relative group cursor-help inline-flex items-center">
                          <span className="inline-flex items-center justify-center w-4 h-4 text-[10px] bg-gray-200 text-gray-700 rounded-full">
                            i
                          </span>
                          <span className="pointer-events-none absolute z-10 hidden group-hover:block left-0 top-full mt-2 w-64 text-xs text-gray-700 bg-white border border-gray-200 rounded-md shadow-lg p-2 whitespace-normal">
                            Для работы с отзывами в OZON нужен тариф Premium
                            Plus. Подключить можно в кабинете продавца.
                          </span>
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {ozonPremium[s.id] === "checking" ? (
                          <span className="text-gray-500">Проверка…</span>
                        ) : ozonPremium[s.id] === "ok" ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200">
                            Подключен
                          </span>
                        ) : ozonPremium[s.id] === "no" ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 border border-red-200">
                            Не подключен
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 border border-yellow-200">
                            Ошибка проверки
                          </span>
                        )}
                        {ozonPremium[s.id] === "no" ? (
                          <a
                            href="https://seller.ozon.ru/app/settings/subscription"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border border-blue-200 text-blue-600 hover:bg-blue-50"
                            title="Подключить Premium Plus в OZON"
                          >
                            Подключить
                          </a>
                        ) : null}
                      </div>
                    </div>
                  </>
                ) : s.platform === "wildberries" ? (
                  <div className="rounded-lg bg-gray-50 px-3 py-2 text-gray-700">
                    API подключен
                  </div>
                ) : s.platform === "yandex" ? (
                  <>
                    <div className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
                      <span className="text-gray-600">Campaign ID</span>
                      <span className="font-mono text-gray-900">
                        {s.credentials?.campaignId || "—"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
                      <span className="text-gray-600">Business ID</span>
                      <span className="font-mono text-gray-900">
                        {s.credentials?.businessId ??
                          s.credentials?.business_id ??
                          "—"}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="rounded-lg bg-gray-50 px-3 py-2 text-gray-700">
                    Нет настроек
                  </div>
                )}
              </div>

              <div className="mt-3 text-xs text-gray-500">
                Создан: {new Date(s.createdAt).toLocaleString()} • Обновлён:{" "}
                {new Date(s.updatedAt).toLocaleString()}
              </div>
            </div>
          ))}
          {stores.length === 0 ? (
            <div className="text-gray-600">Нет магазинов</div>
          ) : null}
        </div>
      )}

      <AddStoreModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export default Stores;
